import React, { useRef, useCallback } from 'react';
import WebCam from '../common-layout-components/WebCam';
import ProblemStatement from '../common-layout-components/ProblemStatement';
import MobileDeviceStatus from '../common-layout-components/MobileDeviceStatus';
import MobileCameraOverlay, { type MobileCameraDetectionSnapshot } from '@/components/interview/MobileCameraOverlay';
import CodeEditor, { type CodeEditorRef } from '@/interview-engine/codeeditor/CodeEditor';
import type { HandDetector } from '@tensorflow-models/hand-pose-detection';
import type { ObjectDetection } from '@tensorflow-models/coco-ssd';

type SupportedLanguage = 'javascript' | 'python' | 'java' | 'c' | 'cpp';

export interface TestCaseData {
  input: string;
  expectedOutput: string;
  description: string;
  fullExecutableCode?: string;
}

export interface Phase2LayoutProps {
  // Candidate name for WebCam
  candidateName?: string;

  // Problem Statement props
  problemData: {
    title: string;
    description: string;
    timeLimit: string;
  } | null;

  // Code Editor props
  testCases: TestCaseData[];
  starterCode: string;
  selectedLanguage: SupportedLanguage;
  onLanguageChange: (language: SupportedLanguage) => void;
  languageDisabled?: boolean;
  isLanguageSwitching?: boolean;
  onCodeSubmit: (code: string, language: string, testResults: any[]) => void;

  // Coding phase timer
  codingTimerLabel?: string;

  // Mobile Camera props
  mobileStream: MediaStream | null;
  latestMobileStreamKey: string;
  detectionModels: {
    handDetector: HandDetector | null;
    objectDetector: ObjectDetection | null;
    error: Error | null;
    hasError: boolean;
    isPreparing: boolean;
    statusMessage: string | null;
  };
  onMobileDetection: (snapshot: MobileCameraDetectionSnapshot) => void;
  workspaceDetected: boolean;
  handsDetected: number;

  // Layout options
  isMobileWebcamRequired?: boolean;

  /** Optional container element for the portal (useful for fullscreen mode) */
  portalContainer?: HTMLElement | null;
}

/**
 * Phase 2 Layout - Coding Phase
 * 
 * Full layout containing:
 * - Left: Code Editor with console
 * - Right: WebCam, Problem Statement, Mobile Camera Overlay
 * 
 * During this phase:
 * - The AI has no active role
 * - The candidate writes code to solve the problem
 */
const Phase2Layout: React.FC<Phase2LayoutProps> = ({
  candidateName,
  problemData,
  testCases,
  starterCode,
  selectedLanguage,
  onLanguageChange,
  languageDisabled = false,
  isLanguageSwitching = false,
  onCodeSubmit,
  codingTimerLabel,
  mobileStream,
  latestMobileStreamKey,
  detectionModels,
  onMobileDetection,
  workspaceDetected,
  handsDetected,
  isMobileWebcamRequired = true,
  portalContainer,
}) => {
  const codeEditorRef = useRef<CodeEditorRef>(null);

  // Handle code submission from CodeEditor
  const handleDsaQuestionSubmit = useCallback((response: { code: string; testCases: any[] }) => {
    console.log('[Phase2Layout] Code submitted:', { codeLength: response.code?.length, testCasesCount: response.testCases?.length });
    onCodeSubmit(response.code, selectedLanguage, response.testCases);
  }, [onCodeSubmit, selectedLanguage]);
  return (
    <React.Fragment>
      {/* Left Column: Code Editor */}
      <div className="max-w-[45%] flex flex-col overflow-hidden flex-1 min-h-0 min-w-0">
        {/* Coding Timer Display */}
        {codingTimerLabel && (
          <div className="mb-2 flex items-center justify-between px-1">
            <span className="text-sm font-medium text-slate-600">Time Remaining</span>
            <span className="text-lg font-mono font-semibold text-slate-800">{codingTimerLabel}</span>
          </div>
        )}
        <div className="flex flex-1 min-h-0 flex-col">
          <CodeEditor
            ref={codeEditorRef}
            placeholder={starterCode}
            language={selectedLanguage}
            selectedLanguage={selectedLanguage}
            testCases={testCases}
            setSelectedLanguage={onLanguageChange}
            handleDsaQuestionSubmit={handleDsaQuestionSubmit}
            onSubmit={() => { }}
            languageDisabled={languageDisabled}
            isLanguageSwitching={isLanguageSwitching}
            portalContainer={portalContainer}
          />
        </div>
      </div>

      {/* Right Column: Problem Statement and mobile overlay */}
      <div className="max-w-[55%] flex flex-col overflow-hidden flex-1 min-h-0 min-w-0 relative">
        <div className="flex flex-1 min-h-0 flex-col gap-5 md:gap-2 lg:gap-2.5 xl:gap-4">
          {/* Problem Statement */}
          <div className="flex-5 min-h-0 flex overflow-hidden">
            <div className="flex-1 min-h-0 flex overflow-hidden rounded-xl bg-white shadow-sm">
              {problemData ? (
                <div className="flex-1 overflow-y-auto">
                  <ProblemStatement
                    instructionVisible={true}
                    problemStatement={problemData.description}
                    problemTitle={problemData.title}
                  />
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400">
                  Loading problem...
                </div>
              )}
            </div>
          </div>
          {isMobileWebcamRequired ? (
            <div className="flex-5 min-h-0 min-w-0 flex overflow-hidden">
              <div className="flex flex-1 min-h-0 min-w-0 gap-4 rounded-xl bg-white p-0 shadow-sm overflow-hidden">
                <div className="flex-3 min-h-0 min-w-0">
                  <div className="relative h-full w-full overflow-hidden rounded-lg bg-black">
                    {/* Mobile Camera */}
                    {mobileStream ? (
                      <>
                        <div className="absolute inset-0 overflow-hidden">
                          <div className="w-full h-[167%] -translate-y-[40%]">
                            <MobileCameraOverlay
                              key={latestMobileStreamKey}
                              stream={mobileStream}
                              handDetector={detectionModels.handDetector}
                              objectDetector={detectionModels.objectDetector}
                              detectionError={detectionModels.error}
                              enableDetections={!detectionModels.hasError}
                              flipHorizontal={false}
                              containerClassName="h-full w-full"
                              videoClassName="h-full w-full object-cover"
                              onDetectionUpdate={onMobileDetection}
                              mode="interview"
                            />
                          </div>
                        </div>

                        {/* Minimal Status Overlay */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
                          <MobileDeviceStatus
                            mobileCameraStream={mobileStream}
                            workspaceDetected={workspaceDetected}
                            handsDetected={handsDetected > 0}
                          />
                        </div>

                        {detectionModels.isPreparing && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-white">
                            <div className="mb-3 h-10 w-10 animate-spin rounded-full border-b-2 border-white" />
                            <p className="text-sm">{detectionModels.statusMessage || "Preparing workspace checks…"}</p>
                          </div>
                        )}
                        {detectionModels.error && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-900/60 px-4 text-center text-white">
                            <p className="text-sm font-semibold">Unable to run detection</p>
                            <p className="text-xs opacity-80">{detectionModels.error.message}</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/80">
                        <div className="h-12 w-12 animate-spin rounded-full border-2 border-white/30 border-t-transparent" />
                        <p className="text-sm">Waiting for phone camera…</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex min-h-0 min-w-0 flex-2 flex-col gap-4 border border-slate-200 rounded-lg p-4 justify-center items-center">
                  <WebCam candidateName={candidateName} variant="round" />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-5 min-h-0 min-w-0 flex overflow-hidden">
              <div className="flex flex-1 min-h-0 min-w-0 rounded-xl bg-white p-4 shadow-sm">
                {/* Empty space in non-mobile webcam mode during coding phase */}
              </div>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default Phase2Layout;
